import {
  Route,
  QueryFrom,
  paramsToRecord,
  QueryParams,
  QueryParamValidator,
} from '@fullstory/routes';
import { isArray } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * A useState like API for sourcing state from query parameters associated with a general Route.
 * It will return the expected types from that route, but these aren't validated at runtime,
 * so you could get other properties that could be in the URL.
 *
 * By defult it will use history.replace, but this may be overridden with the second argument.
 */
export function useQueryParams<
  R extends Route<string, {}, QueryParams, QueryParamValidator<QueryParams> | null>,
>(
  route: R,
): [
  R extends Required<R['_validateQueryParams']> ? QueryFrom<R> : ReturnType<typeof paramsToRecord>,
  (
    values:
      | QueryFrom<R>
      | ((
          prevValues: R extends Required<R['_validateQueryParams']>
            ? QueryFrom<R>
            : ReturnType<typeof paramsToRecord>,
        ) => QueryFrom<R>),
    push?: boolean,
  ) => void,
] {
  type queryParams = R extends Required<R['_validateQueryParams']>
    ? QueryFrom<R>
    : ReturnType<typeof paramsToRecord>;

  const location = useLocation();
  const history = useHistory();

  const params: queryParams = useMemo(() => {
    const params = paramsToRecord(location.search);
    if (route._validateQueryParams) {
      return route._validateQueryParams(params) as queryParams;
    }
    return params as queryParams;
  }, [location.search, route]);

  const setParams = useCallback(
    (
      valuesOrFn:
        | QueryFrom<R>
        | ((
            prevValues: R extends Required<R['_validateQueryParams']>
              ? QueryFrom<R>
              : ReturnType<typeof paramsToRecord>,
          ) => QueryFrom<R>),
      push?: boolean,
    ) => {
      const values = typeof valuesOrFn === 'function' ? valuesOrFn(params) : valuesOrFn;
      const searchParams = new URLSearchParams();
      for (const key in values) {
        const value = values[key];
        if (isArray(value)) {
          value.forEach(v => searchParams.append(key, `${v}`));
        } else if (value != null) {
          searchParams.set(key, `${value}`);
        }
      }

      const historyParams = {
        pathname: location.pathname,
        search: searchParams.toString(),
        state: location.state,
      };
      if (push) history.push(historyParams);
      else history.replace(historyParams);
    },
    [history, location.pathname, location.state, params],
  );

  return [params, setParams];
}
